import { useQuery } from '@apollo/client';
import { Box, styled } from '@mui/material';
import useDatasetMutation from 'FeatureModules/DataImport/hooks/useDatasetMutation';
import { DATA_IMPORT_CATEGORY_CONFIG } from 'FeatureModules/DataImport/utils/dataImportConfig';
import Loader from 'Components/Forms/Loader';
import useDialog from 'hooks/useDialog';
import { DATASETS_PER_PAGE } from 'utils/pagination.utils';
import { ThemeButton, ThemeTypography } from 'DesignSystem';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import { GET_DATASETS } from 'graphql/secure/queries/dataset.queries';
import { GET_BASE_PARTNER_COMPANY } from 'graphql/secure/queries/partner.queries';
import React, { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { EGeoDataStatus, IDataset } from 'FeatureModules/DataImport/types/dataset.types';
import { IBasePartnerCompany } from 'FeatureModules/Partner/types/partner.types';
import { IGraphQlConnection } from 'types/core.types';
import { BooleanParam, StringParam, useQueryParams, withDefault } from 'use-query-params';
import { removeGraphConnections } from 'utils/graphql.utils';
import { DatasetSkeleton } from '../DataImport.styles';
import DataImportOverviewCard from '../DataImportOverviewCard';
import DatasetDetailsCard from '../DatasetDetailsCard';
import DatasetOverviewCard from '../DatasetOverviewCard';
import ActionContainer from 'Components/ActionContainer';
import ErrorState from 'Components/ErrorState';
import FlexBox from 'Components/FlexBox';
import InfiniteScrollWrapper from 'Components/InfiniteScrollWrapper';

const Container = styled('div')(() => ({
  position: 'relative',
  minHeight: 50,
}));

const DatasetContainer = styled(Box)(({ theme }) => ({
  flexWrap: 'wrap',
  display: 'flex',
  gap: theme.spacing(2),
}));

const DatasetSkeletons: FC = () => (
  <>
    {Array.from({ length: DATASETS_PER_PAGE }).map((_, key) => (
      <DatasetSkeleton key={key} variant="rounded" />
    ))}
  </>
);

const GeoDataImport: FC = () => {
  const { id: partnerId } = useParams<{ id?: string }>();
  const [{ new: showNewImportDialog, clusterId }, setQuery] = useQueryParams(
    {
      new: withDefault(BooleanParam, false),
      clusterId: withDefault(StringParam, undefined),
    },
    { removeDefaultsFromUrl: true }
  );
  const navigate = useNavigate();
  const { deleteDataset } = useDatasetMutation();
  const { openDialog } = useDialog();

  const { data, loading, error, fetchMore } = useQuery<{ datasets: IGraphQlConnection<IDataset> }>(
    GET_DATASETS,
    {
      variables: {
        ...(partnerId
          ? {
              filters: {
                ...(partnerId ? { ownedById: partnerId } : {}),
              },
            }
          : {}),
        first: DATASETS_PER_PAGE,
      },
    }
  );

  const { data: partnerData } = useQuery<{ partner: IBasePartnerCompany }>(
    GET_BASE_PARTNER_COMPANY,
    {
      variables: {
        id: partnerId,
      },
      skip: !partnerId,
    }
  );
  const [datasetDetails, setDatasetDetails] = useState<IDataset>();

  const datasets = data ? removeGraphConnections(data.datasets) : [];
  const categoryConfig = DATA_IMPORT_CATEGORY_CONFIG[0];

  const handleClickUpload = () => {
    openDialog({
      type: 'CREATE_DATASET_DECISION',
      props: { datasetOwnedBy: partnerData?.partner },
    });
  };

  const handleClickViewDataset = (datasetId: string) => navigate(`datasets/${datasetId}`);

  const handleClickDeleteDataset = (dataset: IDataset) => {
    openDialog({
      type: 'ALERT',
      props: {
        title: 'Delete dataset',
        itemTitle: datasets.find(item => item.id === dataset.id)?.title,
        text: (
          <FlexBox flexDirection="column" gap={2}>
            <ThemeTypography variant="BODY_MEDIUM">
              Are you sure you want to delete this dataset? This action will permanently remove all
              data in this dataset and cannot be undone.
            </ThemeTypography>
            <ThemeTypography variant="BODY_MEDIUM">
              Note that the farms and origin clusters linked to this dataset will remain in your
              account. If you also want to delete those, please visit the sites library.
            </ThemeTypography>
          </FlexBox>
        ),
        onSubmit: async () => {
          await deleteDataset(dataset);
          setDatasetDetails(undefined);
        },
        displayCloseButton: true,
        onCancel: () => undefined,
        onClose: () => undefined,
        cancelText: 'Cancel',
        submitText: 'Delete',
      },
    });
  };

  const handleClickExportDataset = async (geoDataId: string) => {
    openDialog({
      type: 'DOWNLOAD_GEO_DATA',
      props: {
        geoDataId,
        disableGeojson:
          datasetDetails?.status === EGeoDataStatus.VALIDATION_FAILED ||
          datasetDetails?.status === EGeoDataStatus.VERIFICATION_FAILED_WITH_WARNINGS,
        validationOnly:
          datasetDetails?.status === EGeoDataStatus.VERIFICATION_FAILED_WITH_ERRORS ||
          datasetDetails?.status === EGeoDataStatus.VERIFICATION_FAILED_WITH_WARNINGS ||
          datasetDetails?.status === EGeoDataStatus.COMPLETED
            ? false
            : true,
      },
    });
  };

  const handleFetchMoreDatasets = () => {
    if (data?.datasets?.pageInfo?.hasNextPage) {
      fetchMore({
        variables: {
          after: data.datasets.pageInfo.endCursor,
        },
      });
    }
  };

  useEffect(() => {
    if (showNewImportDialog) {
      // Remove the query param from the URL to avoid showing the dialog again on refresh
      setQuery({ new: false }, 'replaceIn');
      openDialog({
        type: 'CREATE_DATASET_DECISION',
        props: { datasetOwnedBy: partnerData?.partner, defaultClusterId: clusterId },
      });
    }
  }, [showNewImportDialog, partnerData, clusterId, openDialog, setQuery]);

  if (loading) {
    return <Loader />;
  }
  if (error) {
    return <ErrorState />;
  }

  return (
    <Container>
      {datasets.length > 0 ? (
        <>
          <ActionContainer>
            <ThemeButton
              size="medium"
              color="PRIMARY"
              startIcon={<Icon name="plus" color="WHITE" />}
              onClick={handleClickUpload}
            >
              Add new dataset
            </ThemeButton>
          </ActionContainer>

          <Box mt={4}>
            <Box gap={2} display="flex">
              <InfiniteScrollWrapper
                ScrollWrapperComponent={DatasetContainer}
                LoadingIndicator={DatasetSkeletons}
                hasMore={!!data?.datasets?.pageInfo?.hasNextPage}
                next={handleFetchMoreDatasets}
              >
                {datasets.map(dataset =>
                  dataset ? (
                    <DatasetOverviewCard
                      key={dataset.id}
                      issues={dataset.issues || []}
                      dataset={dataset}
                      selected={datasetDetails?.id === dataset.id}
                      onClick={() => setDatasetDetails(dataset)}
                    />
                  ) : undefined
                )}
              </InfiniteScrollWrapper>

              {datasetDetails !== undefined && datasetDetails && (
                <DatasetDetailsCard
                  dataset={datasetDetails}
                  buttonText="View farms"
                  issues={datasetDetails.issues || []}
                  onClickDataset={handleClickViewDataset}
                  onDownloadDataset={handleClickExportDataset}
                  onDeleteDataset={() => handleClickDeleteDataset(datasetDetails)}
                />
              )}
            </Box>
          </Box>
        </>
      ) : (
        <DataImportOverviewCard
          title={`Add your ${categoryConfig.dataLabel} geo-data to enable tracing of impact along your supply chains`}
          onAddTableClick={handleClickUpload}
        />
      )}
    </Container>
  );
};

export default GeoDataImport;
